import { Cassette } from '../../domain/cassette';
import { Operator } from '../../domain/operator';
import { PlageHoraire } from '../../domain/plage-horaire';
import { Pompe } from '../../domain/pompe';
import { ProcessMeasure } from '../../domain/process-mesure';
import { Task } from '../../domain/task';
import { mapper } from '../../tools/mapping/mapper';
import { ColorService } from '../../tools/ui/color.service';
import * as _ from 'lodash';
import { CassetteEntity, OperatorEntity, PompeEntity, PlageHoraireEntity, TaskEntity } from '../entity';
import type { AppDB } from '../db';

export class OperatorRepository {
    constructor(private readonly database: AppDB) {}


    async getOperatorEntityById(id: number) {
        return await this.database.operators.where('Id').equals(id).first(async oe => {
            return oe;
        });
    }

    async getOperatorByIdWithChildren(id: number) {
        return await this.database.operators.where('Id').equals(id).first(async oe => {
            const o = mapper.map(oe, OperatorEntity, Operator);
            if (oe?.CassetteIds) {
                o.CassetteList = await Promise.all(
                    await this.database.cassettes.where('Id').anyOf(oe.CassetteIds).toArray(ces => {
                        return ces.map(async ce => {
                            const c = mapper.map(ce, CassetteEntity, Cassette);
                            await this.database.pompes.where('Id').equals(ce.PompeId!).first(pe => {
                                c.Pompe = mapper.map(pe, PompeEntity, Pompe);
                            })
                            if (ce.PlageHoraireIds) {
                                for (let i = 0; i < ce.PlageHoraireIds.length; i++) {
                                    const phe = await this.database.getPlageHoraireEntityById(ce.PlageHoraireIds[i]);
                                    const ph = mapper.map(phe, PlageHoraireEntity, PlageHoraire)
                                    ph.Color = ColorService.getRandomPastelColor();
                                    c.PlageHoraireList.push(ph);
                                }
                            }
                            return c;
                        });
                    })
                );
            }
            if (oe?.TaskIds) {
                o.TaskList = await Promise.all(
                    await this.database.tasks.where('Id').anyOf(oe.TaskIds).toArray(ies => {
                        return ies.map(async ie => {
                            const t = mapper.map(ie, TaskEntity, Task);
                            t.Color = ColorService.getRandomPastelColor();
                            return t;
                        });
                    })
                );
            }
            return o;
        });
    }

    async attachOperator(operator: Operator, measureId: number) {
        let operatorId: number;
        const oe = mapper.map(operator, Operator, OperatorEntity);

        operatorId = await this.database.operators.add(oe);
        const pe = await this.database.getProcessMeasureEntityById(measureId);
        await this.database.processMeasures.update(measureId, {
            OperatorIds: pe?.OperatorIds ? [...pe.OperatorIds, operatorId] : [operatorId]
        });

        if (operator.TaskList) {
            for (let i = 0; i < operator.TaskList.length; i++) {
                await this.database.attachTask(operator.TaskList[i], operatorId);
            }
        }

        if (operator.CassetteList) {
            for (let i = 0; i < operator.CassetteList.length; i++) {
                await this.database.attachCassetteToOperator(operator.CassetteList[i], operatorId);
            }
        }

        return operatorId;
    }

    async deleteOperator(process: ProcessMeasure, op: Operator) {

        const pe = await this.database.getProcessMeasureEntityByCode(process.Code)
        for (let o = 0; o < pe!.OperatorIds?.length!; o++) {

            const oe = await this.database.getOperatorEntityById(pe!.OperatorIds![o])
            if (oe?.FirstName === op.FirstName && oe.LastName === op.LastName) {

                for (let n = 0; n < oe?.CassetteIds?.length!; n++) {

                    const ce = await this.database.getCassetteEntityById(oe!.CassetteIds![n])
                    for (let l = 0; ce?.PlageHoraireIds && l < ce.PlageHoraireIds.length; l++) {
                        const phe = await this.database.getPlageHoraireEntityById(ce?.PlageHoraireIds[l]!)
                        await this.database.plageHoraires.where('Id').equals(phe?.Id!).delete();
                    }
                    await this.database.cassettes.where('Id').equals(ce?.Id!).delete();
                    await this.database.pompes.where('Id').equals(ce?.PompeId!).delete();
                }
                if (oe?.TaskIds) {
                    this.database.clearTaskyId(oe?.TaskIds!);
                }
                await this.database.operators.where('Id').equals(oe?.Id!).delete();
                _.remove(pe!.OperatorIds!, x => { return x === oe?.Id })
                await this.database.processMeasures.update(pe!.Id!, {
                    OperatorIds: pe!.OperatorIds!
                });
            }
        }

    }

    async clearOperatorById(ids: number[]) {
        await this.database.operators.where('Id').anyOf(ids).toArray(async oe => {
            for (let i = 0; i < oe.length; i++) {
                if (oe && oe[i].Id !== undefined) {
                    await this.database.operators.where('Id').equals(oe[i].Id!).delete();
                }
            }
        })
    }
}
